import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismCard, PrismMessage } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import Content from 'lumen/components/hotel/reviews/review-detail/content';
import Header from 'lumen/components/hotel/reviews/review-detail/header';
import Response from 'lumen/components/hotel/reviews/review-detail/response';
import type { AiUsage, Review } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { TranslationRequest } from 'lumen/types/review-manager';

import styles from './review-detail.module.css';

interface HotelReviewsReviewDetailReviewDetailSignature {
  Args: {
    aiUsageData: AiUsage | null;
    onAiUsageChange?: () => void;
    review: Review;
  };
}

type TranslationRequestResponse = {
  detected_language: string;
  translations: TranslationRequest;
};

type TranslationTaskResult =
  | {
      detectedLanguage: string;
      hasFailed?: false;
      translations: TranslationRequest;
    }
  | {
      detectedLanguage: '';
      hasFailed: true;
      translations: Record<string, never>;
    };

export default class HotelReviewsReviewDetail extends Component<HotelReviewsReviewDetailReviewDetailSignature> {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked showTranslatedVersion = false;
  // Note: currently not used in the template. Consider removing later if it doesn't prove useful
  @tracked translatedTextLanguage = '';
  @tracked translations: TranslationRequest = {};

  // Store the last review ID to detect changes
  lastReviewId?: string;

  resetState = modifier((_element, [review]: [Review]): void => {
    if (this.lastReviewId !== review.id) {
      this.cleanupTranslationState();
      this.lastReviewId = review.id!;
    }
  });

  requestTranslationTask = task(
    async (
      textsToTranslate: TranslationRequest,
      targetLanguage: string,
    ): Promise<TranslationTaskResult> => {
      // Build request with only fields that have content
      const dataToTranslate = Object.fromEntries(
        Object.entries(textsToTranslate).filter(([, value]) => value?.trim()),
      );

      // If no fields have content, return early
      if (Object.keys(dataToTranslate).length === 0) {
        return { translations: {}, detectedLanguage: '', hasFailed: true };
      }

      try {
        const url = formatUrl(
          `/apigateway/v1/app/review-manager/translate?subscription_id=${this.hotelAndMarketService.hotel.id!}`,
          {},
          this.ajax.host,
        );

        const translateResponse =
          await this.ajax.post<TranslationRequestResponse>(url, {
            data: {
              to_language: targetLanguage,
              texts: dataToTranslate,
            },
            contentType: 'application/json',
          });

        return {
          hasFailed: false,
          detectedLanguage: translateResponse.detected_language,
          translations: translateResponse.translations,
        };
      } catch (error) {
        this.errorTracking.captureException(error);

        return {
          translations: {},
          detectedLanguage: '',
          hasFailed: true,
        };
      }
    },
  );

  get hasCachedTranslation(): boolean {
    return Object.keys(this.translations).length > 0;
  }

  get isTranslating(): boolean {
    return this.requestTranslationTask.isRunning;
  }

  get showTranslateButtons(): boolean {
    const userLanguage = this.getBaseLanguageCode(
      this.hotelAndMarketService.user.locale,
    );
    const reviewLanguage = this.getBaseLanguageCode(
      this.args.review.languageCode,
    );

    const hasTextToTranslate = Boolean(
      this.args.review.title ||
      this.args.review.content ||
      this.args.review.positive ||
      this.args.review.negative,
    );

    if (!userLanguage || !reviewLanguage) {
      return false;
    }

    return userLanguage !== reviewLanguage && hasTextToTranslate;
  }

  willDestroy(): void {
    super.willDestroy();

    if (this.requestTranslationTask.isRunning) {
      void this.requestTranslationTask.cancelAll();
    }
  }

  private cleanupTranslationState(): void {
    if (this.requestTranslationTask.isRunning) {
      void this.requestTranslationTask.cancelAll();
    }

    this.showTranslatedVersion = false;
    this.translations = {};
    this.translatedTextLanguage = '';
  }

  /**
   * Extracts the base language code (first 2 letters) from a language code
   * Examples: "en" -> "en", "en-us" -> "en", "en_GB" -> "en", "pt-br" -> "pt"
   */
  private getBaseLanguageCode(langCode: string): string {
    if (!langCode) {
      return '';
    }
    return langCode.toLowerCase().slice(0, 2);
  }

  @action
  onRevertTranslationButtonClick(): void {
    this.showTranslatedVersion = false;
  }

  @action
  async onTranslateButtonClick(): Promise<void> {
    if (this.hasCachedTranslation) {
      this.showTranslatedVersion = true;
      return;
    }

    const result = await this.requestTranslationTask.perform(
      {
        title: this.args.review.title,
        content: this.args.review.content,
        positive: this.args.review.positive,
        negative: this.args.review.negative,
      },
      this.hotelAndMarketService.user.locale,
    );

    this.usageAnalytics.track('Review Translation Requested', {
      eventTrigger: 'review-detail-card',
      product: 'review-manager',
      originalLanguage: this.args.review.languageCode,
      translatedLanguage: this.hotelAndMarketService.user.locale,
    });

    if (result.hasFailed) {
      return;
    }

    this.translations = result.translations;
    this.translatedTextLanguage = result.detectedLanguage;
    this.showTranslatedVersion = true;
  }

  <template>
    <div {{this.resetState @review}}>
      <PrismCard @contentLayout="noPadding" class="p-600">
        <:content>
          <article class="flex flex-col">
            <Header
              @review={{@review}}
              @showTranslatedVersion={{this.showTranslatedVersion}}
              @translatedTitle={{this.translations.title}}
            />
            {{#unless @review.isBookingdotcomScoreOnly}}
              <div class="mt-800">
                <Content
                  @isTranslating={{this.isTranslating}}
                  @onRevert={{this.onRevertTranslationButtonClick}}
                  @onTranslate={{this.onTranslateButtonClick}}
                  @review={{@review}}
                  @showTranslateButtons={{this.showTranslateButtons}}
                  @showTranslatedVersion={{this.showTranslatedVersion}}
                  @translations={{this.translations}}
                />
              </div>
            {{/unless}}
          </article>

          {{#if @review.isBookingdotcomScoreOnly}}
            <div
              class="mt-400 flex items-center justify-center
                {{styles.response-section}}"
              data-test-id="score-only-placeholder"
            >
              <PrismMessage
                @description={{t "review_manager.score_only.description"}}
                @illustration="100x100_comment"
              />
            </div>
          {{else}}
            <div class="mt-400 {{styles.response-section}}">
              <Response
                @aiUsageData={{@aiUsageData}}
                @onAiUsageChange={{@onAiUsageChange}}
                @review={{@review}}
              />
            </div>
          {{/if}}
        </:content>
      </PrismCard>
    </div>
  </template>
}
